/**
 * FollowerCountBadge Component
 *
 * Displays the number of users following a fair as a badge with social proof.
 * Issue #1313: Fair following feature
 *
 * Features:
 * - Compact display with icon
 * - Responsive sizing (small, medium, large)
 * - Tooltip on hover
 * - Accessible (ARIA labels, keyboard navigation)
 * - Smart number formatting (1.2K, 10K, etc.)
 */

import React from 'react';
import { Users } from 'lucide-react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export interface FollowerCountBadgeProps {
  followerCount: number;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Format follower count for display
 * - 0-999: Show exact number
 * - 1,000-999,999: Show with K suffix (e.g., "1.2K")
 * - 1,000,000+: Show with M suffix (e.g., "1.2M")
 */
const formatFollowerCount = (count: number): string => {
  if (count < 1000) {
    return count.toString();
  } else if (count < 1000000) {
    const thousands = count / 1000;
    return thousands % 1 === 0
      ? `${Math.floor(thousands)}K`
      : `${thousands.toFixed(1)}K`;
  } else {
    const millions = count / 1000000;
    return millions % 1 === 0
      ? `${Math.floor(millions)}M`
      : `${millions.toFixed(1)}M`;
  }
};

/**
 * Get follower text (singular/plural)
 */
const getFollowerText = (count: number): string => {
  return count === 1 ? 'follower' : 'followers';
};

export const FollowerCountBadge: React.FC<FollowerCountBadgeProps> = ({
  followerCount,
  size = 'medium',
  className = '',
}) => {
  const { isEnabled: fairFollowingEnabled, isLoading: flagLoading } = useFeatureFlag('fair_following');

  // Hide component when feature flag is disabled (Issue #1470)
  if (!flagLoading && !fairFollowingEnabled) {
    return null;
  }

  // Size-specific styling
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-2.5 py-1',
    large: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    small: 12,
    medium: 14,
    large: 16,
  };

  const formattedCount = formatFollowerCount(followerCount);
  const followerText = getFollowerText(followerCount);
  const tooltipText = `${followerCount.toLocaleString()} ${followerText} following this fair`;

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full bg-gray-100 text-gray-700 font-medium transition-colors hover:bg-gray-200 ${sizeClasses[size]} ${className}`}
      title={tooltipText}
      aria-label={tooltipText}
      role="status"
    >
      <Users
        size={iconSizes[size]}
        className="text-gray-600"
        aria-hidden="true"
      />
      <span>
        {formattedCount}
      </span>
    </div>
  );
};

export default FollowerCountBadge;
